import { z } from "zod";
// ============================================================================
// Domain constants & enums
// ============================================================================
/** 18 fingerprint patterns per DCPPI / CLAUDE.md. Order is canonical. */
export const PATTERNS = [
    "ulnar_loop",
    "radial_loop",
    "spiral_whorl",
    "target_whorl",
    "elongated_whorl",
    "composite_whorl",
    "imploding_whorl",
    "double_loop",
    "central_pocket",
    "lateral_pocket",
    "falling_loop",
    "simple_arch",
    "arch_with_ulnar",
    "arch_with_radial",
    "tented_arch",
    "enclosed_arch",
    "accidental",
    "malformation",
];
export const PatternSchema = z.enum(PATTERNS);
export const PATTERN_FAMILY = {
    ulnar_loop: "loop",
    radial_loop: "loop",
    falling_loop: "loop",
    central_pocket: "loop",
    lateral_pocket: "loop",
    spiral_whorl: "whorl",
    target_whorl: "whorl",
    elongated_whorl: "whorl",
    composite_whorl: "whorl",
    imploding_whorl: "whorl",
    double_loop: "whorl",
    accidental: "whorl",
    simple_arch: "arch",
    arch_with_ulnar: "arch",
    arch_with_radial: "arch",
    tented_arch: "arch",
    enclosed_arch: "arch",
    malformation: "malformation",
};
/** Expected number of deltas per pattern family. */
export const EXPECTED_DELTAS = {
    ulnar_loop: 1,
    radial_loop: 1,
    falling_loop: 1,
    central_pocket: 1,
    lateral_pocket: 1,
    spiral_whorl: 2,
    target_whorl: 2,
    elongated_whorl: 2,
    composite_whorl: 2,
    imploding_whorl: 2,
    double_loop: 2,
    accidental: 2,
    simple_arch: 0,
    arch_with_ulnar: 0,
    arch_with_radial: 0,
    tented_arch: 0,
    enclosed_arch: 0,
    malformation: 0,
};
/** 10 finger positions, canonical naming. */
export const FINGERS = [
    "R1", // Ibu Jari Kanan
    "R2", // Telunjuk Kanan
    "R3", // Tengah Kanan
    "R4", // Manis Kanan
    "R5", // Kelingking Kanan
    "L1", // Ibu Jari Kiri
    "L2", // Telunjuk Kiri
    "L3", // Tengah Kiri
    "L4", // Manis Kiri
    "L5", // Kelingking Kiri
];
export const FingerKeySchema = z.enum(FINGERS);
export const FINGER_LABEL_ID = {
    R1: "Ibu Jari Kanan",
    R2: "Telunjuk Kanan",
    R3: "Tengah Kanan",
    R4: "Manis Kanan",
    R5: "Kelingking Kanan",
    L1: "Ibu Jari Kiri",
    L2: "Telunjuk Kiri",
    L3: "Tengah Kiri",
    L4: "Manis Kiri",
    L5: "Kelingking Kiri",
};
export function fingerHand(f) {
    return f.startsWith("R") ? "right" : "left";
}
/** Roles. */
export const ROLES = ["super_admin", "owner", "agent", "scan_station", "klien"];
export const RoleSchema = z.enum(ROLES);
/** Scope: DCPPI direct vs franchisor-scoped. */
export const SCOPE_TYPES = ["dcppi", "franchisor"];
/** Klien record lifecycle. */
export const KLIEN_STATUS = [
    "uploaded",
    "stitching",
    "stitched",
    "analyzing",
    "analyzed",
    "ready_for_review",
    "approving",
    "approved",
    "reporting",
    "reported",
    "flagged",
    "voided",
];
/** Per-finger sub-status (independent of klien status). */
export const FINGER_STATUS = [
    "pending",
    "stitched",
    "stitch_failed",
    "analyzed",
    "vision_failed",
    "approved",
    "flagged",
];
/** Three scan angles per finger. */
export const SCAN_SIDES = ["F", "L", "R"];
/** Report packages per CLAUDE.md. */
export const REPORT_TIERS = ["singkat", "sedang", "lengkap"];
// ============================================================================
// Core types — finger analysis
// ============================================================================
export const Point2DSchema = z.object({
    x: z.number(),
    y: z.number(),
});
export const DeltaSchema = z.object({
    x: z.number(),
    y: z.number(),
    label: z.enum(["kiri", "kanan"]),
});
/** Per-finger analysis output (from LLM, normalized). */
export const FingerAnalysisSchema = z.object({
    finger: FingerKeySchema,
    pattern: PatternSchema,
    ridge_count_left: z.number().int().min(0),
    ridge_count_right: z.number().int().min(0),
    num_deltas: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    delta_position: z.enum(["kiri", "kanan", "kedua_sisi", "tidak_ada"]).optional(),
    core: Point2DSchema.nullable(),
    deltas: z.array(DeltaSchema),
    tabrakan: z.array(Point2DSchema).default([]),
    sulur_putus: z.array(Point2DSchema).default([]),
    confidence: z.number().min(0).max(1),
    ciri_visual: z.string().optional(),
    /** algorithm_version pinned per analysis. Never re-run unless explicit. */
    algorithm_version: z.string(),
    /** Source of this analysis — AI or human-corrected. */
    source: z.enum(["ai", "human", "ai_then_human"]),
    /** Validation: per family, deltas/ridges must match expected. */
    validation_warnings: z.array(z.string()).default([]),
});
/** A klien record. */
export const KlienRecordSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    franchisor_id: z.string().uuid().nullable(),
    scope_type: z.enum(SCOPE_TYPES),
    batch_id: z.string(),
    full_name: z.string().min(1).max(255),
    dob: z.string(),
    parent_name: z.string().optional(),
    parent_phone: z.string().optional(),
    school_name: z.string().optional(),
    klien_photo_url: z.string().url().nullable(),
    is_left_handed: z.boolean().default(false),
    consent_at: z.string().datetime().nullable(),
    scan_at: z.string().datetime(),
    status: z.enum(KLIEN_STATUS),
    algorithm_version: z.string(),
    package_tier: z.enum(REPORT_TIERS),
    delete_after: z.string().datetime(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    /** Optimistic concurrency: incremented on each update. */
    version: z.number().int().min(0),
});
/** Approval event = audit log row. Append-only. */
export const ApprovalEventSchema = z.object({
    id: z.string().uuid(),
    klien_id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    actor_id: z.string().uuid(),
    actor_role: RoleSchema,
    event_type: z.enum([
        "viewed",
        "edited_pattern",
        "edited_ridge",
        "edited_core",
        "edited_delta",
        "approved_finger",
        "approved_klien",
        "flagged_klien",
        "unflagged_klien",
        "voided",
    ]),
    finger: FingerKeySchema.optional(),
    before: z.unknown().optional(),
    after: z.unknown().optional(),
    reason: z.string().optional(),
    /** Hash chain: previous_hash + this_event_payload → this_hash */
    previous_hash: z.string(),
    this_hash: z.string(),
    signed_at: z.string().datetime(),
    /** Signature using AUDIT_LOG_SIGNING_KEY */
    signature: z.string(),
});
/** Final formula output (Layer 5). */
export const ProfileOutputSchema = z.object({
    klien_id: z.string().uuid(),
    /** Brain profile sections — exact structure depends on Bu Emmy's rumus. */
    profil_otak: z.record(z.unknown()),
    gaya_belajar: z.record(z.unknown()),
    kecerdasan_majemuk: z.record(z.unknown()),
    karakter: z.record(z.unknown()),
    rekomendasi_karir: z.array(z.string()),
    /** Optional narrative text per section. */
    narrative: z.record(z.string()).optional(),
    /** Bu Emmy's edits ratio at report time (for V2 retrain target). */
    narrative_edit_ratio: z.number().min(0).max(1).optional(),
    rumus_version: z.string(),
});
//# sourceMappingURL=domain.js.map